/**
 * Application Configuration
 * API endpoints and constants for qs-status frontend
 */

/**
 * care-api (qs-status, careapp branch). In development the Vite proxy routes
 * /status-api to care-api: the remote one, or the local stack's.
 */
export const STATUS_API =
  import.meta.env.DEV
    ? '/status-api'
    : 'https://care-api.emfit.com/api';

export const QS_API =
  import.meta.env.DEV
    ? '/qs-api'
    : 'https://qs-api.emfit.com/api';

export const STORAGE_URL =
  import.meta.env.DEV
    ? '/storage'
    : 'https://qs-api.emfit.com/storage';

export const AUTH_API =
  import.meta.env.DEV
    ? '/auth-api'
    : 'https://care-api.emfit.com';

/**
 * Supported Locales for qs-status (Emfit Care)
 */
export enum Locales {
  da = 'da', // Danish
  de = 'de', // German
  en_US = 'en-US', // English (US)
  es = 'es', // Spanish
  fi = 'fi', // Finnish
  fr = 'fr', // French
  ja = 'ja', // Japanese
  ko = 'ko', // Korean
  nl = 'nl', // Dutch
  no = 'no', // Norwegian
  ru = 'ru', // Russian
  sv = 'sv', // Swedish
  zh_CN = 'zh_CN', // Chinese Simplified
  zh_HK = 'zh_HK', // Chinese Traditional
}

/**
 * Locale Display Names
 */
export const LocaleNames: Record<Locales, string> = {
  [Locales.da]: 'Dansk',
  [Locales.de]: 'Deutsch',
  [Locales.en_US]: 'English (US)',
  [Locales.es]: 'Español',
  [Locales.fi]: 'Suomi',
  [Locales.fr]: 'Français',
  [Locales.ja]: '日本語',
  [Locales.ko]: '한국어',
  [Locales.nl]: 'Nederlands',
  [Locales.no]: 'Norsk',
  [Locales.ru]: 'Русский',
  [Locales.sv]: 'Svenska',
  [Locales.zh_CN]: '简体中文',
  [Locales.zh_HK]: '繁體中文',
};

/**
 * Locale to i18n File Mapping
 */
export const LocalesToLocaleFileMapping: Record<Locales, string> = {
  [Locales.da]: 'da-DK',
  [Locales.de]: 'de-DE',
  [Locales.en_US]: 'en-US',
  [Locales.es]: 'es-ES',
  [Locales.fi]: 'fi-FI',
  [Locales.fr]: 'fr-FR',
  [Locales.ja]: 'ja-JP',
  [Locales.ko]: 'ko-KR',
  [Locales.nl]: 'nl-NL',
  [Locales.no]: 'no-NO',
  [Locales.ru]: 'ru-RU',
  [Locales.sv]: 'sv-SE',
  [Locales.zh_CN]: 'zh-CN',
  [Locales.zh_HK]: 'zh-HK',
};

/**
 * Officially supported languages shown in the UI
 */
export const SUPPORTED_LANGUAGES: { code: Locales; label: string }[] = [
  { code: Locales.da, label: LocaleNames[Locales.da] },
  { code: Locales.en_US, label: LocaleNames[Locales.en_US] },
  { code: Locales.fi, label: LocaleNames[Locales.fi] },
  { code: Locales.nl, label: LocaleNames[Locales.nl] },
  { code: Locales.no, label: LocaleNames[Locales.no] },
  { code: Locales.sv, label: LocaleNames[Locales.sv] },
];

/**
 * Device Status Refresh Interval (ms)
 */
export const STATUS_REFRESH_INTERVAL = 30000; // 30 seconds

/**
 * WebSocket Reconnect Settings
 */
export const WS_RECONNECT_DELAY = 3000; // 3 seconds
export const WS_MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Pagination
 */
export const DEFAULT_PAGE_SIZE = 50;
export const EVENT_LOG_PAGE_SIZE = 100;

/**
 * Date/Time Formats
 */
export const DATE_FORMAT = 'yyyy-MM-dd';
export const TIME_FORMAT = 'HH:mm:ss';
export const DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/**
 * Device Status Thresholds
 */
export const INACTIVE_DEVICE_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
export const WARNING_ABSENCE_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour

/**
 * Chart Configuration
 */
export const CHART_COLORS = {
  primary: '#3b82f6', // blue-500
  success: '#10b981', // green-500
  warning: '#f59e0b', // amber-500
  danger: '#ef4444', // red-500
  info: '#06b6d4', // cyan-500
  secondary: '#6b7280', // gray-500
};

export const MINITREND_DEFAULT_DAYS = 7;
export const TREND_MAX_DAYS = 30;

/**
 * Feature Flags
 */
export const FEATURES = {
  enableHRV: true,
  enableRawData: false,
  enableNightMode: true,
  enableExport: true,
  enableAdvancedFilters: true,
};

/**
 * User Roles
 */
export enum UserRole {
  ADMIN = 'admin',
  STAFF = 'staff',
  VIEWER = 'viewer',
}

export const RolePermissions = {
  [UserRole.ADMIN]: {
    canManageUsers: true,
    canManageDevices: true,
    canManageGroups: true,
    canViewReports: true,
    canConfigureAlerts: true,
    canExportData: true,
  },
  [UserRole.STAFF]: {
    canManageUsers: false,
    canManageDevices: true,
    canManageGroups: false,
    canViewReports: true,
    canConfigureAlerts: true,
    canExportData: true,
  },
  [UserRole.VIEWER]: {
    canManageUsers: false,
    canManageDevices: false,
    canManageGroups: false,
    canViewReports: true,
    canConfigureAlerts: false,
    canExportData: false,
  },
};
